import type { GlobalFieldId } from '#layers/data-model/types/schema/ids';
import { mergeAttributes, Node } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import DisplayTemplateFieldRefNodeView from '#layers/data-model/components/DisplayTemplateFieldRef/NodeView.vue';

const ARRAY_FIELD_ID_PATTERN = /^\s*\[/;

export function serializeDisplayTemplateFieldId(fieldId: GlobalFieldId | GlobalFieldId[] | null | undefined): string | undefined {
	if (!fieldId) return undefined;
	return Array.isArray(fieldId) ? JSON.stringify(fieldId) : fieldId;
}

function isArrayFieldIdPayload(rawFieldId: string) {
	return ARRAY_FIELD_ID_PATTERN.test(rawFieldId);
}

export function parseDisplayTemplateFieldId(rawFieldId: string | undefined): {
	fieldId: GlobalFieldId | GlobalFieldId[] | null;
	rawFieldId: string | null;
} {
	if (!rawFieldId) {
		return {
			fieldId: null,
			rawFieldId: null,
		};
	}

	if (!isArrayFieldIdPayload(rawFieldId)) {
		return {
			fieldId: rawFieldId as GlobalFieldId,
			rawFieldId: null,
		};
	}

	try {
		const parsed = JSON.parse(rawFieldId);

		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((entry) => typeof entry === 'string')) {
			return {
				fieldId: parsed as GlobalFieldId[],
				rawFieldId: null,
			};
		}
	}
	catch {
		// Preserve malformed array-shaped payloads as unresolved refs.
	}

	return {
		fieldId: null,
		rawFieldId,
	};
}

export const DisplayTemplateFieldRef = Node.create({
	name: 'ref',
	group: 'inline',
	inline: true,
	atom: true,

	selectable: false,
	draggable: true,

	addAttributes() {
		return {
			fieldId: {
				default: null,
				parseHTML: (element) => parseDisplayTemplateFieldId(element.dataset.fieldId).fieldId,
				renderHTML: (attributes) => {
					const serializedFieldId = serializeDisplayTemplateFieldId(attributes.fieldId);

					if (!serializedFieldId) return;

					return {
						'data-field-id': serializedFieldId,
					};
				},
			},
			rawFieldId: {
				default: null,
				parseHTML: (element) => parseDisplayTemplateFieldId(element.dataset.fieldId).rawFieldId,
				renderHTML: (attributes) => {
					if (attributes.fieldId) return;
					if (!attributes.rawFieldId) return;

					return {
						'data-field-id': attributes.rawFieldId,
					};
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: `span[data-type="${this.name}"]`,
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ['span', mergeAttributes({ 'data-type': this.name }, HTMLAttributes)];
	},

	addNodeView() {
		return VueNodeViewRenderer(DisplayTemplateFieldRefNodeView);
	},
});
